import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import Skeleton from 'react-loading-skeleton';
// import { useMemo } from 'react';

import Typography from '~/shared/components/Typography';
import Card from '~/shared/components/Card';
import cloudFIllGrowthAction from '~/shared/assets/icons/cloudfill1.svg';
import cloudFIllShrinkAction from '~/shared/assets/icons/cloudfill2.svg';
import cloudFIllAverageUtilization from '~/shared/assets/icons/cloudfill3.svg';
import ArrowCircleUp from '~/shared/assets/icons/arrowcircleup.svg';
import ArrowCircleDown from '~/shared/assets/icons/arrowcircledown.svg';
import ArrowCircleDownOrange from '~/shared/assets/icons/arrowcircledownOrange.svg';
import { useXFetch } from '~/shared/utils/useXFetch';
import { LastDayResponse } from '~/shared/types/api';

import { calculatePercentageChange, isNegative, makePositive } from '../common/helpers';

type LastDayCardProps = {
  value: number;
  percentage: number;
  type: 'GA' | 'SA' | 'AU';
  CloudIcon: typeof cloudFIllGrowthAction;
  ArrowIcon: typeof cloudFIllGrowthAction;
  isLoading?: boolean;
};

function LastDayCard({
  value,
  type,
  percentage,
  CloudIcon,
  ArrowIcon,
  isLoading,
}: LastDayCardProps) {
  const cloudBG =
    type === 'GA' ? 'bg-light-lime-green' : type === 'SA' ? 'bg-light-purple' : 'bg-light-orange';
  const title =
    type === 'GA' ? 'Growth Actions' : type === 'SA' ? 'Shrink Actions' : 'Average Utilization';
  const arrowDirection = isNegative(percentage) ? 'rotate-0' : 'rotate-180';
  return (
    <div className="border border-[#F0F4FF] h-[152px] w-full rounded-[9px] bg-card p-[20px]">
      <div className=" flex flex-col w-fit justify-between h-full">
        <div className="flex gap-5 items-center">
          <div
            className={clsx(
              'w-[60px] h-[60px] flex items-center justify-center rounded-[11.25px]',
              cloudBG
            )}
          >
            <CloudIcon className="w-[45px]" />
          </div>
          <div>
            <div className="flex items-end">
              {isLoading ? (
                <Skeleton
                  containerClassName="h-[32px] w-full block"
                  className="h-[32px] leading-[unset!important]"
                  baseColor="#DCE5F3"
                  highlightColor="#FFFF"
                />
              ) : (
                <>
                  <Typography variant="H2" fontWeight={700}>
                    {value}
                  </Typography>
                  {type === 'AU' && (
                    <Typography variant="p2" color="text-grey-2">
                      %
                    </Typography>
                  )}
                </>
              )}
            </div>
            <Typography variant="p2" fontWeight={600}>
              {title}
            </Typography>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <ArrowIcon className={clsx('w-[32px]', arrowDirection)} />
          {isLoading ? (
            <Skeleton
              containerClassName="h-[32px] w-[60px] block"
              className="h-[32px] leading-[unset!important]"
              baseColor="#DCE5F3"
              highlightColor="#FFFF"
            />
          ) : (
            <Typography variant="H3">{makePositive(percentage)}%</Typography>
          )}

          <Typography variant="p2" color="text-grey-2" fontWeight={600}>
            Change from previous 24 hours
          </Typography>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="w-full h-full flex justify-center items-center text-[12px]">No Results</div>
  );
}

function LastDay() {
  const api = useXFetch();
  const { data, error, isLoading } = useQuery({
    queryKey: ['getLastDay'],
    queryFn: () => api<LastDayResponse>('/api/v1/opt-log/last-day'),
    refetchOnWindowFocus: false,
  });

  const { lastDay, previousDay } = data || {};

  if (error || (!data && !isLoading && !lastDay && !previousDay)) {
    return <EmptyState />;
  }

  return (
    <Card className="mb-[22px]">
      <Typography variant="H5" className="py-[12px]">
        Last Day
      </Typography>{' '}
      <div className="flex gap-4 mt-[10px]">
        <LastDayCard
          value={lastDay?.growthActions ?? 0}
          type="GA"
          percentage={
            lastDay && previousDay
              ? calculatePercentageChange(previousDay.growthActions, lastDay.growthActions)
              : 0
          }
          CloudIcon={cloudFIllGrowthAction}
          ArrowIcon={ArrowCircleUp}
          isLoading={isLoading}
        />
        <LastDayCard
          value={lastDay?.shrinkActions ?? 0}
          type="SA"
          percentage={
            lastDay && previousDay
              ? calculatePercentageChange(previousDay.shrinkActions, lastDay.shrinkActions)
              : 0
          }
          CloudIcon={cloudFIllShrinkAction}
          ArrowIcon={ArrowCircleDown}
          isLoading={isLoading}
        />
        <LastDayCard
          value={lastDay?.avgUtilization ?? 0}
          type="AU"
          percentage={
            lastDay && previousDay
              ? calculatePercentageChange(previousDay.avgUtilization, lastDay.avgUtilization)
              : 0
          }
          CloudIcon={cloudFIllAverageUtilization}
          ArrowIcon={ArrowCircleDownOrange}
          isLoading={isLoading}
        />
      </div>
    </Card>
  );
}

export default LastDay;
